import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import config from './config.js';
import { getIdentityProviders } from './services/sso';
import {
  AutoSsoSuppressKey,
  AUTO_SSO_GUARD_KEY,
  AUTO_SSO_GUARD_WINDOW_MS,
} from './constants/authorization';

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden>
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

const ProviderIcon = ({ providerId }) => {
  if (providerId === 'google') return <GoogleIcon />;
  return <KeyRound className="w-4 h-4" />;
};

/**
 * Decide whether to skip the login page and bounce straight to SSO: returns the
 * IdP alias to redirect to when the realm has exactly one identity provider,
 * else null. Returns null (staying on the page) when `?manual`/`?login=select`
 * is present, when the post-logout suppression flag is set, or when the 15s
 * loop guard is still hot. Stamps the guard as a side effect when it commits.
 */
export function autoRedirectAlias(providers) {
  const params = new URLSearchParams(window.location.search);
  if (params.has('manual') || params.get('login') === 'select') return null;

  // Not consumed on read — see constants/authorization.js.
  if (sessionStorage.getItem(AutoSsoSuppressKey)) return null;

  if (providers.length !== 1) return null;

  const now = Date.now();
  const last = Number(sessionStorage.getItem(AUTO_SSO_GUARD_KEY) || 0);
  if (now - last < AUTO_SSO_GUARD_WINDOW_MS) return null;
  sessionStorage.setItem(AUTO_SSO_GUARD_KEY, String(now));

  return providers[0].alias;
}

function Login() {
  const { signIn, error } = useAuth();
  const [idps, setIdps] = useState([]);
  const [pendingIdp, setPendingIdp] = useState(null);
  const [localError, setLocalError] = useState(null);

  // Shared by the IdP buttons, the generic fallback, and the single-IdP
  // auto-redirect, so all paths get the same busy state and the same catch that
  // returns to the manual page if the provider can't be reached.
  const startSsoRedirect = useCallback(
    async (idpHint) => {
      setLocalError(null);
      setPendingIdp(idpHint ?? '__sso__');
      try {
        await signIn(idpHint);
      } catch (err) {
        console.error('SSO login failed:', err);
        setLocalError('Could not start sign-in. Please try again.');
        setPendingIdp(null);
      }
    },
    [signIn],
  );

  useEffect(() => {
    getIdentityProviders().then((providers) => {
      setIdps(providers);
      const alias = autoRedirectAlias(providers);
      if (alias) startSsoRedirect(alias);
    });
  }, [startSsoRedirect]);

  const busy = pendingIdp !== null;
  const shownError = localError || error;

  return (
    <div className="flex justify-center items-center min-h-screen bg-app relative">
      <div
        className="bg-surface shadow-lg rounded-lg p-8 flex flex-col items-center"
        style={{ maxWidth: '400px', width: '100%' }}
      >
        {/* Logo and Branding (matches navbar) */}
        <div className="flex items-center gap-3 mb-4">
          {config.brandLogoUrl && (
            <img
              src={config.brandLogoUrl}
              alt={config.brandName}
              className="select-none dark:invert"
              style={{
                height: `${48 * config.brandLogoScale}px`,
                width: 'auto',
                maxWidth: '200px',
                objectFit: 'contain',
              }}
            />
          )}
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[18px] font-bold font-sans tracking-tight text-fg leading-none"
              style={{
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              {config.brandName}
            </span>
            <span
              className="text-[18px] font-medium font-sans tracking-wide text-fg leading-none pl-4"
              style={{
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              {config.brandTagline}
            </span>
          </div>
        </div>

        {shownError && (
          <div
            className="bg-danger-soft border border-danger text-danger px-4 py-2 rounded text-sm mb-4 w-full text-center"
            role="alert"
          >
            {shownError}
          </div>
        )}

        <div className="w-full flex flex-col gap-2 mt-2">
          {idps.length > 0 ? (
            idps.map((idp) => (
              <button
                key={idp.alias}
                type="button"
                onClick={() => startSsoRedirect(idp.alias)}
                disabled={busy}
                className="w-full px-4 py-2 border border-brand text-brand rounded-lg hover:bg-brand-soft transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ProviderIcon providerId={idp.provider_id} />
                {pendingIdp === idp.alias
                  ? 'Redirecting...'
                  : `Sign in with ${idp.display_name}`}
              </button>
            ))
          ) : (
            <button
              type="button"
              onClick={() => startSsoRedirect()}
              disabled={busy}
              className="w-full px-4 py-2 border border-brand text-brand rounded-lg hover:bg-brand-soft transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <KeyRound className="w-4 h-4" />
              {busy ? 'Redirecting...' : 'Sign in with SSO'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
